import MuiButton from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import { alpha, CircularProgress } from '@mui/material';
import type { colorType } from '../../theme';
import { useTheme } from '../../styles/theme';

interface Props {
  /** When true, the text in the button is NOT capitalized. */
  lower?: boolean;
  /** The color of the button. */
  customColor?: keyof colorType;
  /** The size of the component. small is equivalent to the dense button styling. */
  small?: boolean;
  /** The size of the component. tiny is a very small button used for toolbars. */
  tiny?: boolean;
  /** Adds a border around the component. */
  border?: boolean;
  /** Whether button is disabled. */
  disabled?: boolean;
  /** Whether to remove the border around the button */
  borderless?: boolean;
  /** Uses large radius if true */
  pill?: boolean;
}

type ColorProps = {
  theme: any;
  customColor?: keyof colorType;
  small?: boolean;
  tiny?: boolean;
  border?: boolean;
  disabled?: boolean;
  borderless?: boolean;
  pill?: boolean;
};

const getColors = ({ theme, small, tiny, customColor, border, disabled, borderless, pill }: ColorProps) => {
  let buttonBackground = theme?.button?.background?.[customColor as string] || theme.button.background.default;
  let buttonText = theme?.button?.text?.[customColor as string] || theme.button.text.default;

  if (customColor === 'primary') {
    buttonBackground = theme.primary;
    buttonText = 'white';
  }

  const properties = {
    padding: '5px 12px',
    fontSize: '12px',
    height: 'initial',
    fontFamily: 'Inter',
    backgroundColor: buttonBackground,
    color: buttonText,
    borderRadius: pill ? '10rem' : '7px',
    fontWeight: '500',
    minWidth: '32px',
    border: `1px solid ${theme.line.light}`,
    '&:hover': { backgroundColor: alpha(buttonBackground, 0.8), cursor: 'pointer' },
    svg: {
      fontSize: 'initial',
    },
  };

  if (customColor === 'grey') properties.backgroundColor = theme.grey.standard;

  if (small) {
    properties.height = `27px`;
    properties.fontSize = '11px';
    if (!pill) properties.borderRadius = '6px';
    properties.svg.fontSize = '2ch !important';
  }

  if (tiny) {
    properties.height = `24px`;
    properties.padding = '0 10px';
    properties.fontSize = '12px';
    properties.minWidth = '0';
    properties.fontWeight = '500';
    properties.border = '0';
    properties.borderRadius = '6px';
    properties['&:hover'] = { backgroundColor: alpha(theme.button.background.toolbar, 0.08), cursor: 'pointer' };
    properties.svg.fontSize = '1.8ch !important';
  }

  if (border) {
    properties.border = `1px solid ${theme.line.light}`;
  }

  if (borderless) {
    properties.border = '0';
    properties['&:hover'] = { backgroundColor: alpha(theme.button.background.toolbar, 0.08), cursor: 'pointer' };
  }

  if (disabled) {
    properties.backgroundColor = alpha('#000000', 0.04);
  }

  return properties;
};

const customProps = [
  'soft',
  'borderless',
  'active',
  'lower',
  'small',
  'tiny',
  'customColor',
  'softCustomColor',
  'border',
  'pill',
];

export const Button = styled(MuiButton, {
  name: 'telButton',
  slot: 'Root',
  shouldForwardProp: (prop) => !customProps.includes(prop.toString()),
  skipSx: true,
})<Props>(({ small, tiny, lower, borderless, customColor, border, disabled, pill }) => {
  const theme = useTheme();
  return {
    ...getColors({ theme, small, borderless, tiny, customColor, border, disabled, pill }),
    textTransform: lower ? 'none' : 'uppercase',
  };
});

interface LoadingProps {
  customColor?: keyof colorType;
  loading?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  children: React.ReactNode;
  small?: boolean;
  startIcon?: React.ReactNode;
  minWidth?: string;
  minHeight?: string;
  fullWidth?: boolean;
  size?: 'small' | 'medium' | 'large';
  dataIntercomTarget?: string;
}

export const LoadingButton: React.FC<LoadingProps> = (props) => {
  const theme = useTheme();
  return (
    <Button
      data-intercom-target={props?.dataIntercomTarget}
      customColor={props?.customColor}
      startIcon={props.startIcon && !props.loading ? props.startIcon : undefined}
      onClick={typeof props.onClick === 'function' && !props.loading ? props.onClick : undefined}
      small={props?.small}
      fullWidth={props?.fullWidth || false}
      size={props?.size}
      sx={{
        minWidth: props?.minWidth ? `${props.minWidth} !important` : 'initial',
        minHeight: props?.minHeight ? `${props.minHeight} !important` : 'initial',
      }}
      disabled={props?.disabled}
    >
      {props?.loading ? (
        <CircularProgress sx={{ color: !props?.customColor ? theme.text.standard : 'white' }} size={14} thickness={6} />
      ) : (
        props.children
      )}
    </Button>
  );
};
